import React from "react";

/**
 * "Show Comparison Period" toggle + mode selector, shared between tiles that offer
 * period-over-period comparison (Attribute Sum Line Chart, Resource Utilization Chart).
 *
 * Markup/behavior must stay identical to the original Attribute Sum Line Chart
 * implementation this was extracted from — do not make per-tile visual tweaks here;
 * use the `children` slot for tile-specific extras (e.g. a previous-periods count
 * selector) instead.
 */
const ComparisonPeriodControl = ({
  enabled,
  onEnabledChange,
  mode,
  onModeChange,
  modeOptions,
  limitToCurrentProgress,
  onLimitToCurrentProgressChange,
  loading = false,
  disabled = false,
  disabledReason = "",
  children,
}) => {
  const isInteractionDisabled = loading || disabled;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Show Comparison Period
        </label>
        <button
          type="button"
          role="switch"
          aria-checked={!!enabled}
          onClick={() => onEnabledChange(!enabled)}
          disabled={isInteractionDisabled}
          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            enabled ? "bg-primary-600" : "bg-gray-200"
          } ${isInteractionDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          <span
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              enabled ? "translate-x-4" : "translate-x-0"
            }`}
          />
        </button>
      </div>
      {disabled && disabledReason && (
        <p className="text-xs text-gray-500">{disabledReason}</p>
      )}
      {enabled && (
        <>
          <div className="flex gap-4">
            {modeOptions.map((option) => (
              <label
                key={option.id}
                className="inline-flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="comparisonModeLine"
                  value={option.id}
                  checked={mode === option.id}
                  onChange={() => onModeChange(option.id)}
                  className="focus:ring-primary-500"
                  style={{ accentColor: "#0066cc" }}
                  disabled={isInteractionDisabled}
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
          <div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!limitToCurrentProgress}
                onChange={(e) => onLimitToCurrentProgressChange(e.target.checked)}
                className="rounded border-gray-300 focus:ring-primary-500"
                style={{ accentColor: "#0066cc" }}
                disabled={isInteractionDisabled}
              />
              <span className="text-sm font-medium text-gray-700">
                Limit previous periods to current progress
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              When enabled, previous periods are cut off at the same point the current
              period has reached so far (e.g. through August), instead of showing their
              full, already-complete length.
            </p>
          </div>
          {children}
        </>
      )}
    </div>
  );
};

export default ComparisonPeriodControl;
